import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import type { BankDetailsUpdateInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank.details.update.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { JurisdictionTypeEnum } from '@zaoblako/shared/modules/client/enums/jurisdiction-type.enum';
import { BankService } from '@zaoblako/web/core/services/catalog/bank.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableBankDetailsComponent } from '@zaoblako/web/shared/dumbs/table-bank-details/table-bank-details.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';

@Component({
  selector: 'crm-bank-details-add-modal',
  standalone: true,
  imports: [CommonModule, ButtonComponent, TableBankDetailsComponent, SpinnerComponent, JoinPipe],
  templateUrl: './bank-details-add-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BankDetailsAddModalComponent {
  public bankDetails: BankDetailsUpdateInterface[] = [];
  public contragentId: number;
  public companyId: number;
  public banks: BankFindInterface[] = [];
  public jurisdiction: JurisdictionTypeEnum = null;
  public loading = false;

  public errors: string[] = [];

  public get showDefaultBankDetail() {
    return !!this.companyId;
  }

  public constructor(
    private readonly config: DynamicDialogConfig,
    private readonly bankService: BankService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly contragentService: ContragentService,
    private readonly companyService: CompanyService,
    private readonly ref: DynamicDialogRef,
    private readonly errorService: ErrorService
  ) {
    this.bankDetails = config.data.bankDetails;
    if (config.data?.contragentId) {
      this.contragentId = config.data.contragentId;
    }
    if (config.data?.companyId) {
      this.companyId = config.data.companyId;
    }
    if (config.data?.jurisdiction) {
      this.jurisdiction = config.data.jurisdiction;
    }
    if (!this.bankDetails?.length) {
      this.addBankDetail();
    }
  }

  public searchBank(name: string) {
    this.bankService.search({ search: name }).subscribe((banks) => {
      this.banks = banks;
      this.cdRef.markForCheck();
    });
  }

  public addBankDetail() {
    const bankDetail = <BankDetailsUpdateInterface>{};
    this.bankDetails.push(bankDetail);
    this.bankDetails = [...this.bankDetails];
    this.cdRef.markForCheck();
  }

  public saveBankDetails() {
    this.errors = [];

    this.bankDetails.forEach((bankDetail, index) => {
      if (this.jurisdiction !== JurisdictionTypeEnum.FOREIGN) {
        if (!bankDetail.bank) {
          this.errors.push(`${index + 1} строчка: Банк не указан`);
        }
        if (
          !bankDetail.checkingAccount?.length ||
          bankDetail.checkingAccount?.length !== 20 ||
          !/^\d+$/.test(bankDetail.checkingAccount)
        ) {
          this.errors.push(`${index + 1} строчка: Рассчетный счет должен состоять из 20 цифр`);
        }
      } else {
        if (!bankDetail.checkingAccount) {
          this.errors.push(`${index + 1} строчка: Рассчетный счет  не указан`);
        }
      }
    });

    if (this.errors?.length) {
      return;
    }

    this.loading = true;
    this.cdRef.markForCheck();

    let method: Observable<ContragentFindInterface | CompanyFindInterface>;
    if (this.contragentId) {
      method = this.contragentService.patch<ContragentFindInterface, ContragentFindInterface>({
        id: this.contragentId,
        bankDetails: this.bankDetails
      });
    } else if (this.companyId) {
      method = this.companyService.patch<CompanyFindInterface, CompanyFindInterface>({
        id: this.companyId,
        bankDetails: this.bankDetails
      });
    }

    method.subscribe({
      next: ({ bankDetails }) => {
        this.errorService.addSuccessMessage('Реквизиты обновлены!');
        this.loading = false;
        this.cdRef.markForCheck();
        this.ref?.close(bankDetails);
      },
      error: () => {
        this.loading = false;
        this.cdRef.markForCheck();
      }
    });
  }
}
